import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Search } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
/* sem fuso no texto = UTC (Mongo); sempre exibe no horário do Brasil */
const dt = (iso) => { try { return new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(String(iso)) ? iso : `${iso}Z`).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch (e) { return '-'; } };
const STATUS_CLS = {
  DELIVERED: 'bg-emerald-100 text-emerald-700', PAID: 'bg-emerald-100 text-emerald-700', DONE: 'bg-emerald-100 text-emerald-700', EXPLODED: 'bg-orange-100 text-orange-700',
  PENDING: 'bg-slate-100 text-slate-600', PROCESSING: 'bg-amber-100 text-amber-700', OPEN: 'bg-sky-100 text-sky-700',
  FAILED: 'bg-rose-100 text-rose-700', MANUAL_REVIEW: 'bg-rose-200 text-rose-800', ABORTED: 'bg-slate-200 text-slate-700',
};
export const Badge = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${STATUS_CLS[s] || 'bg-slate-100 text-slate-600'}`}>{s}</span>;

/* Tabela genérica com busca por KAKO ID — recargas, jogadores, coletas, saquinhos, ciclos, logs. */
const Table = ({ url, columns, filterKey, params = {}, extra, testid, reloadKey }) => {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({});
  const [q, setQ] = useState('');
  const load = useCallback(async (filtro) => {
    try {
      const r = await api.get(url, { params: { ...params, limit: 200, ...(filtro && filterKey ? { [filterKey]: filtro } : {}) } });
      setRows(r.data.rows || []);
      setMeta(r.data);
    } catch { toast.error('Erro ao carregar.'); setRows([]); }
  }, [url, filterKey, JSON.stringify(params)]);
  useEffect(() => { load(q); }, [load, reloadKey]);
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        {filterKey && (
          <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex items-center gap-1">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={filterKey === 'q' ? 'KAKO ID ou apelido' : 'KAKO ID'} className="h-9 w-44 rounded-lg border px-2 text-sm" data-testid={`${testid}-filter`} />
            <Button type="submit" size="sm" variant="outline"><Search className="h-4 w-4" /></Button>
          </form>
        )}
        <Button size="sm" variant="outline" onClick={() => load(q)} data-testid={`${testid}-reload`}><RefreshCw className="h-4 w-4" /></Button>
        {extra?.(meta, () => load(q))}
        {rows && <span className="ml-auto text-xs text-slate-500">{rows.length} registros</span>}
      </div>
      {!rows ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div> : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>{columns.map((c) => <th key={c.k} className="px-2 py-2 font-bold">{c.l}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={columns.length} className="px-2 py-6 text-center text-slate-400">Nada por aqui.</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.order_id || r.reward_id || r.grab_id || r.cycle_id || r.kako_id || i} className="border-t" data-testid={`${testid}-row`}>
                  {columns.map((c) => <td key={c.k} className="px-2 py-1.5 align-top">{c.r ? c.r(r, () => load(q)) : (r[c.k] ?? '-')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const WbRecharges = (p) => (
  <Table url="/admin/wb/recharges" filterKey="kako_id" testid="wb-admin-recharges" {...p} columns={[
    { k: 'created_at', l: 'Quando', r: (r) => dt(r.created_at) }, { k: 'order_id', l: 'Pedido' }, { k: 'kako_id', l: 'KAKO ID' },
    { k: 'nickname', l: 'Apelido' }, { k: 'coins', l: 'Moedas', r: (r) => <b>{fmt(r.coins)}</b> },
    { k: 'witch_coins', l: '→ Moedas da Bruxa', r: (r) => <b className="text-violet-700">{fmt(r.witch_coins)}</b> },
    { k: 'fund_contribution', l: '→ Fundo', r: (r) => fmt(r.fund_contribution) }, { k: 'day', l: 'Dia' }]} />
);

export const WbPlayers = (p) => (
  <Table url="/admin/wb/players" filterKey="q" testid="wb-admin-players" {...p} columns={[
    { k: 'nickname', l: 'Apelido', r: (r) => <>{r.nickname}{r.is_test && <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-black text-violet-700">TESTE</span>}</> },
    { k: 'kako_id', l: 'KAKO ID' }, { k: 'pops_total', l: 'Balões', r: (r) => <b>{fmt(r.pops_total)}</b> },
    { k: 'balance', l: 'Saldo 🧙', r: (r) => <b className="text-violet-700">{fmt(r.balance)}</b> },
    { k: 'witch_earned', l: 'Ganhas 🧙', r: (r) => fmt(r.witch_earned) }, { k: 'recharged_coins', l: 'Recarregou', r: (r) => fmt(r.recharged_coins) },
    { k: 'wins_small', l: 'Prêmios', r: (r) => `${fmt(r.wins_small)} · ${fmt(r.wins_big)} 👑` }, { k: 'prizes_total', l: 'Moedas ganhas', r: (r) => <b className="text-amber-700">{fmt(r.prizes_total)}</b> },
    { k: 'joined_at', l: 'Entrou', r: (r) => dt(r.joined_at) }]} />
);

const RES_CLS = { WIN_BIG: 'bg-amber-100 text-amber-800', WIN_SMALL: 'bg-emerald-100 text-emerald-700', EMPTY: 'bg-slate-100 text-slate-500' };
export const WbPops = (p) => (
  <Table url="/admin/wb/pops" filterKey="kako_id" testid="wb-admin-pops" {...p} columns={[
    { k: 'created_at', l: 'Quando', r: (r) => dt(r.created_at) }, { k: 'nickname', l: 'Apelido' }, { k: 'kako_id', l: 'KAKO ID' },
    { k: 'result', l: 'Resultado', r: (r) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${RES_CLS[r.result] || 'bg-rose-100 text-rose-700'}`}>{r.result || r.status}</span> },
    { k: 'prize', l: 'Prêmio', r: (r) => (r.prize ? <b className="text-amber-700">{fmt(r.prize)}</b> : '-') }, { k: 'cost', l: 'Custo 🧙', r: (r) => fmt(r.cost) },
    { k: 'balance_after', l: 'Saldo após', r: (r) => fmt(r.balance_after) }, { k: 'rng', l: 'RNG', r: (r) => (r.rng != null ? r.rng : '-') },
    { k: 'fulfillment', l: 'Entrega', r: (r) => (r.fulfillment ? <Badge s={r.fulfillment} /> : '-') }, { k: 'fail_reason', l: 'Falha', r: (r) => r.fail_reason || '-' },
    { k: 'is_test', l: 'Teste?', r: (r) => (r.is_test ? 'sim' : '') }]} />
);

export const WbRewards = (p) => (
  <Table url="/admin/wb/rewards" filterKey="kako_id" testid="wb-admin-rewards" {...p}
    extra={(meta, reload) => (
      <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="wb-admin-fulfill-run"
        onClick={async () => { try { const r = await api.post('/admin/wb/fulfillment/run'); toast.success(`Entrega rodada: ${r.data.delivered} ok, ${r.data.failed} falhas`); reload(); } catch { toast.error('Erro ao rodar entrega.'); } }}>
        Rodar entrega agora
      </Button>
    )}
    columns={[
      { k: 'created_at', l: 'Quando', r: (r) => dt(r.created_at) }, { k: 'kako_id', l: 'KAKO ID' },
      { k: 'result', l: 'Prêmio', r: (r) => (r.result === 'WIN_BIG' ? '👑 Balão dourado' : 'Prêmio') },
      { k: 'coins', l: 'Moedas', r: (r) => <b className="text-amber-700">{fmt(r.coins)}</b> }, { k: 'status', l: 'Status', r: (r) => <Badge s={r.status} /> },
      { k: 'attempts', l: 'Tent.' }, { k: 'error', l: 'Erro', r: (r) => r.error || '-' }, { k: 'delivered_at', l: 'Entregue', r: (r) => (r.delivered_at ? dt(r.delivered_at) : '-') },
      { k: 'reward_id', l: '', r: (r) => (['FAILED', 'MANUAL_REVIEW'].includes(r.status)
        ? <Button size="sm" variant="outline" data-testid={`wb-admin-requeue-${r.reward_id}`} onClick={async () => { try { await api.post(`/admin/wb/rewards/${r.reward_id}/requeue`); toast.success('Reenfileirado.'); } catch { toast.error('Erro.'); } }}>Reenviar</Button> : null) }]} />
);

export const WbLogs = (p) => (
  <Table url="/admin/wb/logs" testid="wb-admin-logs" {...p} columns={[
    { k: 'created_at', l: 'Quando', r: (r) => dt(r.created_at) }, { k: 'admin_id', l: 'Admin' }, { k: 'action', l: 'Ação', r: (r) => <b>{r.action}</b> },
    { k: 'target', l: 'Alvo', r: (r) => <span className="font-mono text-[10px]">{String(r.target || '').slice(0, 18)}</span> },
    { k: 'value_before', l: 'Antes', r: (r) => <pre className="max-w-[220px] whitespace-pre-wrap text-[10px]">{typeof r.value_before === 'object' ? JSON.stringify(r.value_before)?.slice(0, 160) : String(r.value_before ?? '-')}</pre> },
    { k: 'value_after', l: 'Depois', r: (r) => <pre className="max-w-[220px] whitespace-pre-wrap text-[10px]">{typeof r.value_after === 'object' ? JSON.stringify(r.value_after)?.slice(0, 160) : String(r.value_after ?? '-')}</pre> },
    { k: 'note', l: 'Nota' }]} />
);
